import api from "../../api/axios";
import { convertKeysToSnakeCase } from "../../utils/caseConverter";

// API calls for the /start flow. Same endpoints as the dashboard, but every
// request is `silent`: /start shows its own inline errors and retries, and the
// global toast is English-only and too wide for a phone. Store payloads only
// carry the fields that are set, because the shared createStore sends missing
// values as the strings "null" / "undefined".

const SILENT = { silent: true };
const MULTIPART = { ...SILENT, headers: { "Content-Type": "multipart/form-data" } };

// The wallet download routes live at the API root, not under /v1.
export function apiOrigin() {
  return new URL(import.meta.env.VITE_API_URL, window.location.origin).origin;
}

export function apiBase() {
  return String(new URL(import.meta.env.VITE_API_URL, window.location.origin)).replace(/\/$/, "");
}

export function walletUrls(customerUuid) {
  const origin = apiOrigin();
  return {
    apple: `${origin}/passes/${customerUuid}/download/pass.pkpass`,
    google: `${origin}/passes/${customerUuid}/download_google/pass.json`,
  };
}

export function publicFlyerUrl(flyerUuid) {
  return `${apiBase()}/public/flyers/${flyerUuid}`;
}

export function publicFormUrl(formUuid) {
  return `${window.location.origin}/f/${formUuid}`;
}

function storeFormData(fields) {
  const formData = new FormData();
  const map = {
    name: "name",
    currency: "currency",
    timeZone: "time_zone",
    backgroundColor: "background_color",
    primaryColor: "primary_color",
    secondaryColor: "secondary_color",
  };
  for (const [key, param] of Object.entries(map)) {
    const value = fields[key];
    if (value !== undefined && value !== null && value !== "") formData.append(param, value);
  }
  if (fields.logo) formData.append("logo", fields.logo, "logo.png");
  return formData;
}

export async function createStore(fields) {
  const { data } = await api.post("/stores", storeFormData(fields), MULTIPART);
  return data;
}

export async function updateStore(storeId, fields) {
  const { data } = await api.put(`/stores/${storeId}`, storeFormData(fields), MULTIPART);
  return data;
}

export async function listPasses(storeId) {
  const { data } = await api.get(`/stores/${storeId}/passes`, SILENT);
  return data || [];
}

function passFormData({ layoutData, stampIcon, backgroundImages, milestoneIconsFormData, previewImage }) {
  const formData = new FormData();
  formData.append("pass", JSON.stringify(convertKeysToSnakeCase(layoutData)));
  if (stampIcon) formData.append("stamp_icon", stampIcon, "stamp-icon.png");
  (backgroundImages || []).forEach((blob, index) => {
    const ext = blob.type === "image/png" ? "png" : "jpg";
    formData.append(`backgroundImage_${index}`, blob, `background-${index}.${ext}`);
  });
  if (milestoneIconsFormData) {
    milestoneIconsFormData.forEach((value, key) => formData.append(key, value));
  }
  if (previewImage) formData.append("preview_image", previewImage, "preview.png");
  return formData;
}

export async function createPass(storeId, payload) {
  const { data } = await api.post(`/stores/${storeId}/passes`, passFormData(payload), MULTIPART);
  return data;
}

export async function updatePass(storeId, passId, payload) {
  const { data } = await api.put(
    `/stores/${storeId}/passes/${passId}`,
    passFormData(payload),
    MULTIPART,
  );
  return data;
}

// Forms (and their flyers) are deleted with the card, and its customers are
// detached from it (backend: Pass has_many forms, dependent: :destroy).
export async function deletePass(storeId, passId) {
  await api.delete(`/stores/${storeId}/passes/${passId}`, SILENT);
}

export async function listForms(storeId) {
  const { data } = await api.get(`/stores/${storeId}/forms`, SILENT);
  return data || [];
}

export async function createForm(storeId, payload) {
  const { data } = await api.post(`/stores/${storeId}/forms`, payload, SILENT);
  return data;
}

export async function updateForm(storeId, formId, payload) {
  const { data } = await api.put(`/stores/${storeId}/forms/${formId}`, payload, SILENT);
  return data;
}

export async function listFlyers(storeId) {
  const { data } = await api.get(`/stores/${storeId}/flyers`, SILENT);
  return data || [];
}

function flyerFormData(flyer, pdf) {
  const formData = new FormData();
  formData.append("flyer", JSON.stringify(flyer));
  if (pdf) formData.append("pdf", pdf, "flyer.pdf");
  return formData;
}

export async function createFlyer(storeId, flyer, pdf) {
  const { data } = await api.post(`/stores/${storeId}/flyers`, flyerFormData(flyer, pdf), MULTIPART);
  return data;
}

// The backend overwrites every attribute on update, so `flyer` must be complete.
export async function updateFlyer(storeId, flyerId, flyer, pdf) {
  const { data } = await api.put(
    `/stores/${storeId}/flyers/${flyerId}`,
    flyerFormData(flyer, pdf),
    MULTIPART,
  );
  return data;
}

export async function checkSetup(storeId) {
  const { data } = await api.get(`/stores/${storeId}/check_setup`, SILENT);
  return data;
}

export async function fetchStoreSilently(storeId) {
  const { data } = await api.get(`/stores/${storeId}`, SILENT);
  return data;
}

// 404 means "no customer with this email" on this endpoint.
export async function findCustomersByEmail(storeId, email) {
  try {
    const { data } = await api.get(`/stores/${storeId}/customers/search`, {
      ...SILENT,
      params: { email },
    });
    return data?.customers || [];
  } catch (error) {
    if (error.response?.status === 404) return [];
    throw error;
  }
}

export async function submitPublicForm(formUuid, submission) {
  const { data } = await api.post(`/public/forms/${formUuid}/submit`, { submission }, SILENT);
  return data;
}

export async function sendPassEmail(storeId, customerId) {
  const { data } = await api.post(`/stores/${storeId}/customers/${customerId}/send_pass`, {}, SILENT);
  return data;
}

// `point` is the absolute balance the card should end on, not a delta.
export async function setCustomerPoint(storeId, customerId, point) {
  const { data } = await api.put(`/stores/${storeId}/customers/${customerId}`, { point }, SILENT);
  return data;
}

export async function createCheckout(storeId, productId, interval) {
  const { data } = await api.post(
    `/stores/${storeId}/checkout`,
    { product_id: productId, interval },
    SILENT,
  );
  return data;
}

export async function detectCountry() {
  try {
    const { data } = await api.get("/public/phone_codes/detect", SILENT);
    return data?.country_code || null;
  } catch {
    return null;
  }
}

export async function updateUserName(name) {
  const { data } = await api.patch("/user", { name }, SILENT);
  return data;
}

export async function saveReferral(promotekitReferralId) {
  const { data } = await api.patch(
    "/user",
    { promotekit_referral_id: promotekitReferralId },
    SILENT,
  );
  return data;
}
